import React, { useRef, useEffect, useState } from "react";
import { FaceMesh } from "@mediapipe/face_mesh";
import { Camera } from "@mediapipe/camera_utils";
import { Modal, Button } from "antd";
import { useNavigate } from "react-router-dom";
import {
  Lightbulb,
  Glasses,
  MoveRight,
  Info,
  CameraIcon,
  LucideScanFace,
  CheckCircle,
  LucideCamera,
  RotateCw,
} from "lucide-react";
import useUserStore from "../../store/useUserStrore";
import "./FaceMeshMeasure.css";

const AVERAGE_IRIS_MM = 13.5;

const captureGuidelines = [
  {
    Icon: CameraIcon,
    text: "Hold the camera directly in front of your face, at eye level.",
  },
  {
    Icon: Lightbulb,
    text: "Use soft, even lighting. Avoid harsh shadows and direct sunlight.",
  },
  {
    Icon: Glasses,
    text: "Remove hats, glasses, and anything covering your face.",
  },
  {
    Icon: LucideScanFace,
    text: "Keep a neutral expression and stay still while facing the camera.",
  },
  {
    Icon: MoveRight,
    text: "Align your face inside the guide. Hold steady once it turns green.",
  },
];

/*
 * Renders either as a standalone page (the /measure route) or as a modal
 * launched from the home search card. In modal mode the parent controls
 * `open`, and destroyOnClose unmounts this component on dismiss so the
 * camera effect's cleanup runs and the webcam is actually released.
 */
const FaceMeshMeasure = ({ asModal = false, open = false, onClose }) => {
  const navigate = useNavigate();
  const { userDetails } = useUserStore();
  const isPremium = userDetails && (userDetails.profileType === "admin" || (userDetails.premiumValidUpto && new Date(userDetails.premiumValidUpto) > new Date()));

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const cameraRef = useRef(null);
  const captureTimeoutRef = useRef(null);

  const [captured, setCaptured] = useState(false);
  const [widthMM, setWidthMM] = useState(null);
  const [, setAligned] = useState(false);
  const [alignmentStable, setAlignmentStable] = useState(false);
  const [snapReady, setSnapReady] = useState(false);
  const [mmPerPixel, setMmPerPixel] = useState(0);
  const [cameraFacingMode, setCameraFacingMode] = useState("user");
  /* Why the camera could not start, if it could not. Without this the
     failure was a console.error and a permanently black panel - the user
     was told nothing at all on the one action this paid feature exists
     for. */
  const [cameraError, setCameraError] = useState("");
  const [cameraAvailability, setCameraAvailability] = useState({
    user: true,
    environment: true,
  });
  const [faceShape, setFaceShape] = useState("oval");
  const [hasSelectedFaceShape, setHasSelectedFaceShape] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 640, height: 480 });
  
  const alignmentStartRef = useRef(null);
  const capturedMmPerPixelRef = useRef(null);
  const measurementHistoryRef = useRef([]);
  const stableMeasurementRef = useRef(null);
  const capturedRef = useRef(false);
  const faceShapeRef = useRef("oval");
  const latestLandmarksRef = useRef(null);
  const alignmentStableRef = useRef(false);
  
  const faceShapeOptions = [
    {
      type: "oval",
      label: "Oval",
      description: "Balanced proportions with soft curves.",
    },
    {
      type: "rectangle",
      label: "Rectangle",
      description: "Sharper angles with a taller frame.",
    },
    {
      type: "circle",
      label: "Round",
      description: "Fuller cheeks with equal width and height.",
    },
  ];

  const capturedNostrilCoordsRef = useRef({
    left: { x: null, y: null },
    right: { x: null, y: null },
  });

  useEffect(() => {
    capturedRef.current = captured;
  }, [captured]);

  useEffect(() => {
    alignmentStableRef.current = alignmentStable;
  }, [alignmentStable]);

  useEffect(() => {
    faceShapeRef.current = faceShape;
  }, [faceShape]);

  useEffect(() => {
    if (!hasSelectedFaceShape) {
      return;
    }

    const updateCanvasDimensions = () => {
      if (!canvasRef.current) return;

      const wrapperElement = wrapperRef.current;
      const width = wrapperElement?.clientWidth || 640;
      const height = wrapperElement?.clientHeight || 480;

      if (
        canvasRef.current.width !== width ||
        canvasRef.current.height !== height
      ) {
        canvasRef.current.width = width;
        canvasRef.current.height = height;
        setCanvasSize({ width, height });
      }
    };

    updateCanvasDimensions();

    let resizeObserver;
    const wrapperElement = wrapperRef.current;
    let cleanup = () => {};

    if (typeof ResizeObserver !== "undefined" && wrapperElement) {
      resizeObserver = new ResizeObserver(updateCanvasDimensions);
      resizeObserver.observe(wrapperElement);
      cleanup = () => {
        resizeObserver?.disconnect();
      };
    } else {
      const handleResize = () => updateCanvasDimensions();
      window.addEventListener("resize", handleResize);
      window.addEventListener("orientationchange", handleResize);
      cleanup = () => {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("orientationchange", handleResize);
      };
    }

    return () => {
      cleanup();
    };
  }, [hasSelectedFaceShape]);

  useEffect(() => {
    if (!hasSelectedFaceShape) {
      if (cameraRef.current) {
        cameraRef.current.stop();
        cameraRef.current = null;
      }
      return;
    }

    let isMounted = true;
    const faceMesh = new FaceMesh({
      locateFile: (file) =>
        `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
    });

    faceMesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: true,
      minDetectionConfidence: 0.7,
      minTrackingConfidence: 0.7,
    });

    function onResults(results) {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!ctx || !canvas) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      const isMobileView =
        typeof window !== "undefined"
          ? window.innerWidth <= 768
          : canvas.width <= 480;
      const shape = faceShapeRef.current;

      const sourceWidth = results.image?.width || canvas.width;
      const sourceHeight = results.image?.height || canvas.height;
      const sourceAspect = sourceWidth / (sourceHeight || 1);
      const canvasAspect = canvas.width / (canvas.height || 1);

      let drawWidth = canvas.width;
      let drawHeight = canvas.height;

      if (sourceAspect > 0 && canvasAspect > 0) {
        if (canvasAspect > sourceAspect) {
          drawWidth = canvas.width;
          drawHeight = drawWidth / sourceAspect;
        } else {
          drawHeight = canvas.height;
          drawWidth = drawHeight * sourceAspect;
        }
      }

      const drawX = (canvas.width - drawWidth) / 2;
      const drawY = (canvas.height - drawHeight) / 2;

      const availableWidth = canvas.width;
      const availableHeight = canvas.height;

      const ovalBaseWidth = 180;
      const ovalBaseHeight = 220;
      const rectangleBaseWidth = 200;
      const rectangleBaseHeight = 230;
      const circleBaseDiameter = 200;

      const scaleMask = (
        baseWidth,
        baseHeight,
        widthFactor = 0.86,
        heightFactor = 0.88
      ) => {
        if (!isMobileView) {
          return { maskWidth: baseWidth, maskHeight: baseHeight };
        }

        const maxWidth = availableWidth * widthFactor;
        const maxHeight = availableHeight * heightFactor;
        const widthScale = maxWidth / baseWidth;
        const heightScale = maxHeight / baseHeight;
        const scale = Math.min(widthScale, heightScale);
        const safeScale =
          !Number.isFinite(scale) || scale <= 0 ? 1 : scale;

        return {
          maskWidth: baseWidth * safeScale,
          maskHeight: baseHeight * safeScale,
        };
      };

      const computeMaskDimensions = () => {
        if (shape === "rectangle") {
          return scaleMask(rectangleBaseWidth, rectangleBaseHeight, 0.88, 0.86);
        }

        if (shape === "circle") {
          const scaled = scaleMask(circleBaseDiameter, circleBaseDiameter, 0.8, 0.8);
          const diameter = Math.min(scaled.maskWidth, scaled.maskHeight);
          return { maskWidth: diameter, maskHeight: diameter };
        }

        return scaleMask(ovalBaseWidth, ovalBaseHeight, 0.86, 0.88);
      };

      const { maskWidth, maskHeight } = computeMaskDimensions();

      const clipPath = (context) => {
        context.beginPath();
        if (shape === "rectangle") {
          const x = centerX - maskWidth / 2;
          const y = centerY - maskHeight / 2;
          const radius = Math.min(maskWidth, maskHeight) * 0.06;
          context.moveTo(x + radius, y);
          context.lineTo(x + maskWidth - radius, y);
          context.quadraticCurveTo(x + maskWidth, y, x + maskWidth, y + radius);
          context.lineTo(x + maskWidth, y + maskHeight - radius);
          context.quadraticCurveTo(
            x + maskWidth,
            y + maskHeight,
            x + maskWidth - radius,
            y + maskHeight
          );
          context.lineTo(x + radius, y + maskHeight);
          context.quadraticCurveTo(
            x,
            y + maskHeight,
            x,
            y + maskHeight - radius
          );
          context.lineTo(x, y + radius);
          context.quadraticCurveTo(x, y, x + radius, y);
        } else {
          context.ellipse(
            centerX,
            centerY,
            maskWidth / 2,
            maskHeight / 2,
            0,
            0,
            Math.PI * 2
          );
        }
      };

      const isInsideMask = (pointX, pointY) => {
        const dx = pointX - centerX;
        const dy = pointY - centerY;

        if (shape === "rectangle") {
          return (
            Math.abs(dx) <= maskWidth / 2 && Math.abs(dy) <= maskHeight / 2
          );
        }

        if (shape === "circle") {
          const radius = maskWidth / 2;
          return dx * dx + dy * dy <= radius * radius;
        }

        return (
          dx * dx / (maskWidth / 2) ** 2 + dy * dy / (maskHeight / 2) ** 2 <= 1
        );
      };

      // Draw dark background
      ctx.fillStyle = "rgba(0, 0, 0, 0.8)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw mask area with video
      ctx.save();
      clipPath(ctx);
      ctx.clip();
      // Mirror only when using front camera; natural for back camera
      if (cameraFacingMode === "user") {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        const mirroredX = canvas.width - drawX - drawWidth;
        ctx.drawImage(results.image, mirroredX, drawY, drawWidth, drawHeight);
      } else {
        ctx.drawImage(results.image, drawX, drawY, drawWidth, drawHeight);
      }
      ctx.restore();

      // Draw face guide in mask
      ctx.save();
      clipPath(ctx);
      ctx.clip();

      ctx.strokeStyle = "rgba(255, 255, 255, 0.3)";
      ctx.lineWidth = 2;

      ctx.beginPath();
      if (shape === "rectangle") {
        const outlineWidth = maskWidth * 0.72;
        const outlineHeight = maskHeight * 0.78;
        const outlineX = centerX - outlineWidth / 2;
        const outlineY = centerY - outlineHeight / 2;
        const radius = Math.min(outlineWidth, outlineHeight) * 0.08;
        ctx.moveTo(outlineX + radius, outlineY);
        ctx.lineTo(outlineX + outlineWidth - radius, outlineY);
        ctx.quadraticCurveTo(
          outlineX + outlineWidth,
          outlineY,
          outlineX + outlineWidth,
          outlineY + radius
        );
        ctx.lineTo(outlineX + outlineWidth, outlineY + outlineHeight - radius);
        ctx.quadraticCurveTo(
          outlineX + outlineWidth,
          outlineY + outlineHeight,
          outlineX + outlineWidth - radius,
          outlineY + outlineHeight
        );
        ctx.lineTo(outlineX + radius, outlineY + outlineHeight);
        ctx.quadraticCurveTo(
          outlineX,
          outlineY + outlineHeight,
          outlineX,
          outlineY + outlineHeight - radius
        );
        ctx.lineTo(outlineX, outlineY + radius);
        ctx.quadraticCurveTo(
          outlineX,
          outlineY,
          outlineX + radius,
          outlineY
        );
      } else {
        const outlineWidth = maskWidth * 0.8;
        const outlineHeight = maskHeight * 0.85;
        ctx.ellipse(
          centerX,
          centerY,
          outlineWidth / 2,
          outlineHeight / 2,
          0,
          0,
          Math.PI * 2
        );
      }
      ctx.stroke();

      ctx.beginPath();
      const eyeRadiusX = isMobileView ? maskWidth * 0.045 : 8;
      const eyeRadiusY = isMobileView ? maskHeight * 0.023 : 5;
      const eyeOffsetX = maskWidth * 0.15;
      const eyeOffsetY = maskHeight * 0.1;
      ctx.ellipse(
        centerX - eyeOffsetX,
        centerY - eyeOffsetY,
        eyeRadiusX,
        eyeRadiusY,
        0,
        0,
        Math.PI * 2
      );
      ctx.ellipse(
        centerX + eyeOffsetX,
        centerY - eyeOffsetY,
        eyeRadiusX,
        eyeRadiusY,
        0,
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "rgba(255, 255, 255, 0.4)";
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(centerX, centerY - maskHeight * 0.05);
      ctx.lineTo(centerX - 5, centerY + maskHeight * 0.1);
      ctx.lineTo(centerX + 5, centerY + maskHeight * 0.1);
      ctx.closePath();
      ctx.stroke();

      ctx.beginPath();
      const mouthRadius = isMobileView ? maskWidth * 0.085 : 15;
      ctx.arc(centerX, centerY + maskHeight * 0.2, mouthRadius, 0, Math.PI);
      ctx.stroke();

      ctx.restore();

      if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
        const landmarks = results.multiFaceLandmarks[0];
        latestLandmarksRef.current = landmarks;
        const flipX = (x) => canvas.width - x;
        const getXY = (id) => {
          if (!landmarks[id]) {
            return { x: 0, y: 0 };
          }
          return {
            x: flipX(landmarks[id].x * canvas.width),
            y: landmarks[id].y * canvas.height,
          };
        };

        const nose = getXY(1);
        const leftEye = getXY(33);
        const rightEye = getXY(263);
        
        if (!nose.x || !nose.y || !leftEye.x || !leftEye.y || !rightEye.x || !rightEye.y) {
          return;
        }
        
        const eyeWidth = Math.abs(rightEye.x - leftEye.x);
        const eyeVerticalDiff = Math.abs(leftEye.y - rightEye.y);

        const insideMask = isInsideMask(nose.x, nose.y);

        const levelRatioByShape = {
          oval: 0.05,
          rectangle: 0.045,
          circle: 0.04,
        };
        const widthRatioByShape = {
          oval: isMobileView
            ? { min: 0.58, max: 0.66 }
            : { min: 0.6, max: 0.64 },
          rectangle: isMobileView
            ? { min: 0.54, max: 0.62 }
            : { min: 0.56, max: 0.6 },
          circle: isMobileView
            ? { min: 0.6, max: 0.7 }
            : { min: 0.62, max: 0.68 },
        };

        const levelThresholdRatio = levelRatioByShape[shape] ?? 0.05;
        const { min: widthMinRatio, max: widthMaxRatio } =
          widthRatioByShape[shape] ?? widthRatioByShape.oval;

        const levelThreshold = maskHeight * levelThresholdRatio;
        const isLevel = eyeVerticalDiff < levelThreshold;
        const eyeWidthMin = maskWidth * widthMinRatio;
        const eyeWidthMax = maskWidth * widthMaxRatio;
        const isIdealEyeWidth = eyeWidth >= eyeWidthMin && eyeWidth <= eyeWidthMax;
        const isAligned = insideMask && isLevel && isIdealEyeWidth;

        setAligned(isAligned);

        const irisLeft1 = getXY(469);
        const irisLeft2 = getXY(471);
        const irisRight1 = getXY(474);
        const irisRight2 = getXY(476);
        
        if (
          !irisLeft1.x ||
          !irisLeft1.y ||
          !irisLeft2.x ||
          !irisLeft2.y ||
          !irisRight1.x ||
          !irisRight1.y ||
          !irisRight2.x ||
          !irisRight2.y
        ) {
          return;
        }
        
        const irisLeftSize = Math.hypot(irisLeft2.x - irisLeft1.x, irisLeft2.y - irisLeft1.y);
        const irisRightSize = Math.hypot(irisRight2.x - irisRight1.x, irisRight2.y - irisRight1.y);
        const irisAveragePx = (irisLeftSize + irisRightSize) / 2;
        const currentMmPerPixel = AVERAGE_IRIS_MM / irisAveragePx;

        setMmPerPixel(currentMmPerPixel);

        const now = Date.now();

        if (isAligned && currentMmPerPixel > 0 && !capturedRef.current) {
          if (!alignmentStartRef.current) {
            alignmentStartRef.current = now;
          }

          const alignedDuration = now - alignmentStartRef.current;

          if (alignedDuration >= 5000) {
            const leftNostril = getXY(327);
            const rightNostril = getXY(98);
            const nostrilDistPx = Math.hypot(
              leftNostril.x - rightNostril.x,
              leftNostril.y - rightNostril.y
            );
            const nostrilWidthMM = nostrilDistPx * currentMmPerPixel;

            measurementHistoryRef.current.push(nostrilWidthMM);
            
            if (measurementHistoryRef.current.length > 10) {
              measurementHistoryRef.current.shift();
            }
            
            const avgMeasurement = measurementHistoryRef.current.reduce((sum, val) => sum + val, 0) / measurementHistoryRef.current.length;
            
            if (Math.abs(nostrilWidthMM - avgMeasurement) < 0.5) {
              stableMeasurementRef.current = nostrilWidthMM;
            }

            capturedNostrilCoordsRef.current = {
              left: leftNostril,
              right: rightNostril,
            };
            capturedMmPerPixelRef.current = currentMmPerPixel;

            setWidthMM(parseFloat((stableMeasurementRef.current || nostrilWidthMM).toFixed(1)));

            setCaptured(true);
            setSnapReady(true);

            setTimeout(() => {
              if (videoRef.current && !videoRef.current.paused) {
                videoRef.current.pause();
              }

              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  const dataURL = canvas.toDataURL("image/png");
                  localStorage.setItem("capturedImage", dataURL);
                });
              });
            }, 100);

            alignmentStartRef.current = null;
          }

        } else {
          alignmentStartRef.current = null;
          setSnapReady(false);
          if (!capturedRef.current) {
            setWidthMM(null);
            if (videoRef.current?.paused) {
              videoRef.current.play();
            }
          }
        }

        if (!isAligned && alignmentStableRef.current) {
          setAlignmentStable(false);
          alignmentStableRef.current = false;
          if (captureTimeoutRef.current) {
            clearTimeout(captureTimeoutRef.current);
            captureTimeoutRef.current = null;
          }
        }
        
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, canvas.width, canvas.height);
        clipPath(ctx);
        ctx.closePath();
        ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
        ctx.fill();
        ctx.restore();
        
        ctx.save();
        clipPath(ctx);
        ctx.strokeStyle =
          isAligned || capturedRef.current ? "#00FF88" : "#ffffffaa";
        ctx.lineWidth = isAligned || capturedRef.current ? 4 : 2;
        if (isAligned || capturedRef.current) {
          ctx.shadowColor = "#00FF88";
          ctx.shadowBlur = 18;
        }
        ctx.stroke();
        ctx.restore();

        ctx.beginPath();
        ctx.moveTo(centerX, centerY - maskHeight / 2);
        ctx.lineTo(centerX, centerY + maskHeight / 2);
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = "rgba(255,255,255,0.5)";
        ctx.stroke();

        if ((isAligned || capturedRef.current) && currentMmPerPixel > 0) {
          const leftNostril =
            capturedNostrilCoordsRef.current?.left ?? getXY(327);
          const rightNostril =
            capturedNostrilCoordsRef.current?.right ?? getXY(98);

          const nostrilDistPx = Math.hypot(
            leftNostril.x - rightNostril.x,
            leftNostril.y - rightNostril.y
          );
          const mmPerPixelUsed =
            capturedMmPerPixelRef.current !== null
              ? capturedMmPerPixelRef.current
              : currentMmPerPixel;

          const nostrilWidthMM = nostrilDistPx * mmPerPixelUsed + 2;

          const midX = (leftNostril.x + rightNostril.x) / 2;
          const midY = (leftNostril.y + rightNostril.y) / 2;

          ctx.beginPath();
          ctx.moveTo(leftNostril.x, leftNostril.y);
          ctx.lineTo(rightNostril.x, rightNostril.y);
          ctx.strokeStyle = "#00FF88";
          ctx.lineWidth = 2;
          ctx.stroke();

          ctx.font = "bold 14px Arial";
          ctx.fillStyle = "#00FF88";
          ctx.textAlign = "center";
          ctx.fillText(`${nostrilWidthMM.toFixed(1)} mm`, midX, midY + 20);
        }
      }
    }

    faceMesh.onResults(onResults);

    const startCamera = async () => {
      if (!videoRef.current) {
        return;
      }

      if (cameraRef.current) {
        try {
          await cameraRef.current.stop();
        } catch (error) {
          console.error("Failed to stop camera", error);
        }
        cameraRef.current = null;
      }

      const facingModeConstraint =
        cameraFacingMode === "environment"
          ? { exact: "environment" }
          : { ideal: "user" };

      const camera = new Camera(videoRef.current, {
        onFrame: async () => {
          // Frames can still fire after unmount/stop, when videoRef is already
          // null and faceMesh is closed, so sending then throws inside MediaPipe.
          if (!isMounted || !videoRef.current || capturedRef.current) {
            return;
          }

          try {
            await faceMesh.send({ image: videoRef.current });
          } catch (error) {
            if (isMounted) {
              console.error("Failed to process camera frame", error);
            }
          }
        },
        width: 640,
        height: 480,
        facingMode: facingModeConstraint,
      });

      cameraRef.current = camera;

      try {
        /*
         * Ask for the camera ourselves before handing off to MediaPipe.
         *
         * MediaPipe's Camera.start() SWALLOWS a getUserMedia rejection and
         * resolves anyway - verified by stubbing getUserMedia to reject with
         * NotAllowedError, which produced no throw, no console error, and a
         * permanently black panel. So `await camera.start()` cannot be used
         * to detect a denied or missing camera.
         *
         * This probe gets the real DOMException, and its tracks are stopped
         * straight away so the Camera helper can acquire the device itself.
         */
        const probe = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facingModeConstraint },
        });
        probe.getTracks().forEach((t) => t.stop());
        if (!isMounted) return;
        setCameraError("");

        await camera.start();

        if (isMounted && cameraFacingMode === "environment") {
          setCameraAvailability((prev) => ({ ...prev, environment: true }));
        }

        if (navigator.mediaDevices?.enumerateDevices) {
          const devices = await navigator.mediaDevices.enumerateDevices();
          if (isMounted) {
            const videoInputs = devices.filter(
              (device) => device.kind === "videoinput"
            );

            let hasFront = false;
            let hasBack = false;

            videoInputs.forEach((device) => {
              const label = device.label?.toLowerCase() || "";
              if (label.includes("front") || label.includes("user")) {
                hasFront = true;
              }
              if (
                label.includes("back") ||
                label.includes("rear") ||
                label.includes("environment")
              ) {
                hasBack = true;
              }
            });

            if (!hasFront && videoInputs.length > 0) {
              hasFront = true;
            }

            if (!hasBack && videoInputs.length > 1) {
              hasBack = true;
            }

            if (
              !hasBack &&
              videoInputs.length > 0 &&
              /iP(hone|ad|od)/i.test(navigator.userAgent || "")
            ) {
              hasBack = true;
            }

            setCameraAvailability({
              user: hasFront,
              environment: hasBack,
            });
          }
        }
      } catch (error) {
        console.error("Failed to start camera", error);
        if (!isMounted) return;

        /* A failed BACK camera falls back to the front one and says
           nothing, which is right - the user still gets a working capture.
           Anything else has to surface, or the panel just stays black. */
        if (cameraFacingMode === "environment") {
          setCameraAvailability((prev) => ({ ...prev, environment: false }));
          setCameraFacingMode("user");
          return;
        }

        const name = error && error.name;
        setCameraError(
          name === "NotAllowedError" || name === "SecurityError"
            ? "Camera access was blocked. Allow camera for this site in your browser settings, then reopen this window."
            : name === "NotFoundError" || name === "OverconstrainedError"
            ? "No camera was found on this device. You can still search by entering measurements yourself."
            : name === "NotReadableError"
            ? "The camera is in use by another app. Close it and try again."
            : "The camera could not be started. Check permissions and try again."
        );
      }
    };

    startCamera();

    return () => {
      isMounted = false;
      if (cameraRef.current) {
        cameraRef.current.stop();
        cameraRef.current = null;
      }
      faceMesh.close();
    };
  }, [cameraFacingMode, hasSelectedFaceShape]);

  const reset = () => {
    setCaptured(false);
    setSnapReady(false);
    setWidthMM(null);
    setAlignmentStable(false);
    alignmentStableRef.current = false;
    setAligned(false);
    setMmPerPixel(0);
    videoRef.current?.play();

    if (asModal) {
      // A full page reload would tear down the whole app from inside a
      // modal. Returning to the shape picker re-runs the camera effect,
      // which is the same clean restart the reload was buying.
      capturedRef.current = false;
      alignmentStartRef.current = null;
      capturedMmPerPixelRef.current = null;
      measurementHistoryRef.current = [];
      stableMeasurementRef.current = null;
      latestLandmarksRef.current = null;
      setHasSelectedFaceShape(false);
      return;
    }

    window.location.reload();
  };

  const handleFaceShapeSelect = (shape) => {
    setFaceShape(shape);
    setHasSelectedFaceShape(true);
    setCaptured(false);
    setSnapReady(false);
    setWidthMM(null);
    setAlignmentStable(false);
    alignmentStableRef.current = false;
    alignmentStartRef.current = null;
    capturedRef.current = false;
  };

  const handleFaceShapeChange = () => {
    if (cameraRef.current) {
      cameraRef.current.stop();
      cameraRef.current = null;
    }
    setHasSelectedFaceShape(false);
    setCaptured(false);
    capturedRef.current = false;
    setSnapReady(false);
    setAlignmentStable(false);
    alignmentStableRef.current = false;
    setAligned(false);
    setWidthMM(null);
    setMmPerPixel(0);
    measurementHistoryRef.current = [];
    stableMeasurementRef.current = null;
    capturedNostrilCoordsRef.current = {
      left: { x: null, y: null },
      right: { x: null, y: null },
    };
  };

  const handleCameraSwitch = (mode) => {
    if (mode === cameraFacingMode) {
      return;
    }

    setCaptured(false);
    capturedRef.current = false;
    setSnapReady(false);
    setAlignmentStable(false);
    alignmentStableRef.current = false;
    setAligned(false);
    setWidthMM(null);
    setMmPerPixel(0);
    measurementHistoryRef.current = [];
    stableMeasurementRef.current = null;
    capturedNostrilCoordsRef.current = {
      left: { x: null, y: null },
      right: { x: null, y: null },
    };
    capturedMmPerPixelRef.current = null;
    alignmentStartRef.current = null;

    setCameraFacingMode(mode);
  };

  const handleSnapAndRedirect = () => {
    const finalMM = widthMM;
    if (!finalMM) {
      alert("No measurement captured yet.");
      return;
    }
    const canvas = canvasRef.current;
    const imageDataURL = canvas.toDataURL("image/png");
    localStorage.setItem("capturedImage", imageDataURL);
    localStorage.setItem(
      "capturedNostrilWidthMM",
      parseFloat(finalMM).toFixed(1)
    );
    // Close first so the modal unmounts and releases the camera before the
    // route change, rather than leaving it open over the results page.
    if (asModal && onClose) onClose();
    navigate(
      `/product-results?mass=${parseFloat(finalMM).toFixed(1)}&from=measure`
    );
  };

  // Manual capture: compute from latest landmarks and store like auto-capture
  const handleManualCapture = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Require readiness similar to auto-capture
    if (mmPerPixel <= 0 || capturedRef.current) return;

    const lm = latestLandmarksRef.current;
    if (!lm || !lm[327] || !lm[98]) return;

    const flipX = (x) => (cameraFacingMode === "user" ? canvas.width - x : x);
    const left = {
      x: flipX(lm[327].x * canvas.width),
      y: lm[327].y * canvas.height,
    };
    const right = {
      x: flipX(lm[98].x * canvas.width),
      y: lm[98].y * canvas.height,
    };

    const nostrilDistPx = Math.hypot(left.x - right.x, left.y - right.y);
    const nostrilWidthMM = nostrilDistPx * mmPerPixel;
    const fixed = parseFloat(nostrilWidthMM.toFixed(1));

    setWidthMM(fixed);
    setCaptured(true);
    setSnapReady(true);

    try {
      videoRef.current?.pause();
    } catch {}

    const dataURL = canvas.toDataURL("image/png");
    localStorage.setItem("capturedImage", dataURL);
    localStorage.setItem("capturedNostrilWidthMM", fixed.toString());
  };

  const faceShapeLabel =
    faceShape.charAt(0).toUpperCase() + faceShape.slice(1);



  if (userDetails && !isPremium) {
    const paywall = (
      <div className="measure-paywall">
        <h2 className="measure-paywall-title">
          Face scanning is on the paid plan
        </h2>
        <p className="measure-paywall-text">
          You can keep searching by data. Scanning adds the measurement step.
        </p>

        {/* One line each. A dt/dd pair per item doubled the height of the
            card for copy that fits on a single line, which left the whole
            dialog looking mostly empty. */}
        <ul className="measure-paywall-list">
          <li>Nostril width measured in millimetres</li>
          <li>Moulds narrowed to your face shape</li>
          <li>Capture saved with the case</li>
        </ul>

        <div className="measure-paywall-actions">
          <Button
            type="primary"
            className="measure-paywall-btn"
            onClick={() => navigate("/plans")}
          >
            View plans
          </Button>
          {asModal && (
            <button
              type="button"
              className="measure-paywall-dismiss"
              onClick={onClose}
            >
              Not now
            </button>
          )}
        </div>
      </div>
    );

    /* Must honour asModal like every other branch below. Returning page
       markup here regardless of `open` made the paywall render inline on
       the home page (MiddleSection mounts this component permanently) and
       stopped the modal from ever opening, because the component bailed
       before reaching the Modal wrapper. */
    if (asModal) {
      return (
        <Modal
          open={open}
          onCancel={onClose}
          /* No antd title bar: "Measure your denture size" above "Face
             scanning is on the paid plan" is the same sentence twice. The
             paywall heading is the title. */
          title={null}
          footer={null}
          centered
          width={460}
          className="measure-modal measure-modal-paywall"
          destroyOnClose
        >
          {paywall}
        </Modal>
      );
    }

    return (
      <div className="face-measure-container">
        <h1>Measure Your Denture Size</h1>
        <div className="tabs">
          <div className="tab-item" onClick={() => navigate("/home")}>
            Search By Data
          </div>
          <div className="tab-item active">Try 3D Camera</div>
        </div>
        {paywall}
      </div>
    );
  }

  if (!hasSelectedFaceShape) {
    const picker = (
      <>
        {!asModal && <h1>Select Your Face Shape</h1>}
        <p>
          Choose the outline that best matches your features. The measurement
          guide adapts to your selection for better alignment.
        </p>
        <div className="face-shape-grid">
          {faceShapeOptions.map((option) => (
            <button
              key={option.type}
              type="button"
              className="face-shape-option"
              onClick={() => handleFaceShapeSelect(option.type)}
            >
              <div className={`face-shape-visual ${option.type}`} />
              <h2>{option.label}</h2>
              <p>{option.description}</p>
            </button>
          ))}
        </div>
      </>
    );

    if (asModal) {
      return (
        <Modal
          open={open}
          onCancel={onClose}
          title="Select your face shape"
          footer={null}
          centered
          width={720}
          className="measure-modal"
          destroyOnClose
        >
          {picker}
        </Modal>
      );
    }

    return <div className="face-measure-container">{picker}</div>;
  }

  const measure = (
    <>
      {!asModal && (
        <>
          <h1>Measure Your Denture Size</h1>
          {/* Page-only: inside a modal these tabs would navigate the page
              out from under the open dialog. */}
          <div className="tabs">
            <div className="tab-item" onClick={() => navigate("/home")}>
              Search By Data
            </div>
            <div className="tab-item active">Try 3D Camera</div>
          </div>
        </>
      )}

      <div className="measure-stage">
        <p className="measure-intro">
          Align your face inside the {faceShapeLabel.toLowerCase()} guide. It
          captures automatically once the outline turns green.
        </p>

        <div className="face-shape-pill">
          <span className="face-shape-indicator">
            {faceShapeLabel} face selected
          </span>
          <button type="button" onClick={handleFaceShapeChange}>
            Change
          </button>
        </div>

        <div className="video-wrapper">
          <div className="camera-toggle">
            <button
              type="button"
              className={`camera-toggle-button ${
                cameraFacingMode === "user" ? "active" : ""
              }`}
              onClick={() => handleCameraSwitch("user")}
              disabled={!cameraAvailability.user}
            >
              Front Camera
            </button>
            <button
              type="button"
              className={`camera-toggle-button ${
                cameraFacingMode === "environment" ? "active" : ""
              }`}
              onClick={() => handleCameraSwitch("environment")}
              disabled={!cameraAvailability.environment}
            >
              Back Camera
            </button>
          </div>
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{ display: "none" }}
          />
          <div ref={wrapperRef} className="canvas-wrapper">
            <canvas
              ref={canvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
            />
            {/* Sits over the canvas rather than replacing it: the element
                has to stay mounted for the refs and the face-mesh loop. */}
            {cameraError && (
              <div className="camera-error" role="alert">
                <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
                  <path
                    d="M12 8.5v5M12 16.6h.01M10.3 3.9 2.6 17.4A2 2 0 0 0 4.3 20.4h15.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p className="camera-error-text">{cameraError}</p>
              </div>
            )}
          </div>
          {!captured && mmPerPixel > 0 && (
            <button
              type="button"
              className="capture-button"
              title="Manual Capture"
              onClick={handleManualCapture}
            >
              CAP
            </button>
          )}
        </div>

        {captured && widthMM && (
          <div className="results">
            <p className="measure-readout">
              <CheckCircle aria-hidden="true" />
              <span>
                Nostril width
                <strong>{widthMM} mm</strong>
              </span>
            </p>

            {snapReady && (
              <div className="measure-actions">
                <button
                  type="button"
                  className="measure-btn measure-btn-primary"
                  onClick={handleSnapAndRedirect}
                >
                  <LucideCamera aria-hidden="true" />
                  <span>See Results</span>
                </button>

                <button
                  type="button"
                  className="measure-btn measure-btn-secondary"
                  onClick={reset}
                >
                  <RotateCw aria-hidden="true" />
                  <span>Retake</span>
                </button>
              </div>
            )}
          </div>
        )}

        <section className="capture-guidelines" aria-labelledby="capture-guidelines-title">
          <div className="capture-guidelines-head">
            <Info aria-hidden="true" />
            <h2 id="capture-guidelines-title">Capture Guidelines</h2>
          </div>
          <ul>
            {captureGuidelines.map(({ Icon, text }) => (
              <li key={text}>
                <Icon aria-hidden="true" />
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </section>

      </div>
    </>
  );

  if (asModal) {
    return (
      <Modal
        open={open}
        onCancel={onClose}
        title="Measure your denture size"
        footer={null}
        centered
        width={1040}
        className="measure-modal"
        /* Unmounts on close so the camera effect's cleanup runs and the
           webcam light actually goes off. */
        destroyOnClose
        /* A stray backdrop click should not kill an in-progress capture. */
        maskClosable={false}
      >
        {measure}
      </Modal>
    );
  }

  return <div className="face-measure-container">{measure}</div>;
};

export default FaceMeshMeasure;
